import classNames from "classnames";
import React, { useMemo } from 'react';
import FilmwebLink from 'src/components/filmweblink/FilmwebLink';
import OptionalPosterInfo from "src/components/posterrow/OptionalPosterInfo";
import PosterImage from "src/components/posterrow/PosterImage";
import { type Movie, type StreamingContent, type WatchableContent } from 'src/lib/movieinfo/movieinfotypes';
import { isMovie, isStreamingContent, isWatchableContent } from "src/lib/movieinfo/typeGuards";
import { IFilmomtale } from 'src/lib/types/Filmomtale';
import cssPosterStyles from "./CSSPoster.module.css";


//#region [Props]
export type PosterProps = {
    className?: string;
    film: IFilmomtale | Movie | StreamingContent | WatchableContent;
    sizes?: string;
    children?: React.ReactNode;
    posterMissingText?: string;
    showMovieTitleIfPosterIsMissing?: boolean;
    hideTitle?: boolean;
    width?: number;
    optionalDisplay?: "year";
    longTitle?: boolean;
    hideKinoklubbBanner?: boolean;
};

//#endregion

//#region [Component]
export default function Poster({ film, className, sizes, posterMissingText = "Plakat mangler", showMovieTitleIfPosterIsMissing = false, hideTitle = false, width, optionalDisplay, longTitle = false, hideKinoklubbBanner = false, children }: PosterProps) {
    const [_filmUrl, _hasPoster, _isKinoklubb] = useMemo<[string | null, boolean, boolean]>(() => {
        if (!film) {
            return [null, false, false];
        }
        let url = "";
        let hasPoster = false;
        let isKinoklubb = false;
        if (isMovie(film)) {
            url = `/film/${film.mainVersionId}`
            hasPoster = !!film.sanityImagePosterUrl;
            isKinoklubb = film.isKinoklubb;
        } else if (isStreamingContent(film)) {
            url = `/streamingguide/${film.isSeries ? "serie" : "film"}/${film.id}`;
            hasPoster = !!film.sanityImagePosterUrl || (film.imagesPoster?.length ?? 0) > 0;
        } else if (isWatchableContent(film)) {
            url = film.isCinemaRelevant ?
                `/film/${film.movieId}`
                : `/streamingguide/${film.isSeries ? "serie" : "film"}/${film.streamingContentId}`;
            hasPoster = !!film.sanityImagePosterUrl || (film.imagesPosterStreaming?.length ?? 0) > 0;
        } else {
            url = `/film/${film.mainVersionEDI}`
            hasPoster = (film.postersV2?.length ?? 0) > 0 || !!film.mainPosterV2;
            isKinoklubb = film.isKinoklubb;
        }
        if (!url) {
            return [null, false, false];
        }
        return [url, hasPoster, isKinoklubb];
    }, [film]);

    if (!_filmUrl) return null;

    return <article className={classNames(className, cssPosterStyles.CSSPoster)} >
        <PosterImage film={film} hasPoster={_hasPoster} showMovieTitleIfPosterIsMissing={showMovieTitleIfPosterIsMissing} filmUrl={_filmUrl} isKinoklubb={_isKinoklubb} hideKinoklubbBanner={hideKinoklubbBanner} posterMissingText={posterMissingText} sizes={sizes} width={width} />
        {!hideTitle && <h1 className={longTitle ? cssPosterStyles["lines-3"]  : ""}>
            <FilmwebLink to={_filmUrl}>
                {film.title}
                <OptionalPosterInfo film={film} optionalDisplay={optionalDisplay} />
            </FilmwebLink>
        </h1>}

        {children}
    </article>;
}
//#endregion
